import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  motion,
  useMotionValue,
  useReducedMotion,
  animate as fmAnimate,
  type PanInfo,
} from 'framer-motion';
import { ArrowRight, X } from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/molecules/Dialog';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { IconButton } from '@/components/atoms/IconButton';
import { StepDots } from '@/components/atoms/StepDots';
import { useIsMobile } from '@/hooks/useIsMobile';
import { motionPressWhileTap } from '@/constants/motionPress';
import { SlideActiveContext } from './SlideActiveContext';
import styles from './WelcomeOnboardingDialog.module.scss';
import type { WelcomeOnboardingDialogProps } from './WelcomeOnboardingDialog.types';

/** Umbral en px o velocidad para considerar el swipe como cambio de paso. */
const SWIPE_DISTANCE_PX = 80;
const SWIPE_VELOCITY = 500;

const SPRING = { type: 'spring' as const, stiffness: 320, damping: 36, mass: 0.9 };

// Reveal por capas del contenido al cambiar de slide: el track tarda
// ~500ms en asentar, por eso esperamos 800ms antes de empezar con el
// título y arrancamos la descripción cuando al título le quedan ~100ms
// (esa pequeña superposición da la sensación de "encadenado" en lugar
// de dos animaciones aisladas).
const TITLE_DELAY_S = 0.8;
const DESCRIPTION_DELAY_S = 1.1;
const TEXT_DURATION_S = 0.4;
const TEXT_EXIT_DURATION_S = 0.2;

/**
 * Tres estados del texto:
 *   - `hidden`:  pre-entrada (`y: 20, opacity: 0`). Se snappa instantáneo;
 *                solo se usa al montar y como "rearmado" post-exit (silencioso
 *                porque el slide ya está fuera de viewport).
 *   - `visible`: estado de reposo del slide activo. Anima desde `hidden`
 *                con el delay configurado por elemento (título primero,
 *                descripción ~300ms más tarde).
 *   - `exiting`: salida del slide previo. Solo fade-out de opacidad, sin
 *                tocar `y`, para que el texto no parezca "caer" mientras
 *                el slide sale del viewport.
 */
type TextAnimState = 'hidden' | 'visible' | 'exiting';

const TITLE_VARIANTS = {
  hidden: { y: 20, opacity: 0, transition: { duration: 0 } },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: TEXT_DURATION_S, delay: TITLE_DELAY_S, ease: 'easeOut' as const },
  },
  exiting: {
    y: 0,
    opacity: 0,
    transition: { duration: TEXT_EXIT_DURATION_S, ease: 'easeOut' as const },
  },
} as const;

const DESCRIPTION_VARIANTS = {
  hidden: { y: 20, opacity: 0, transition: { duration: 0 } },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: TEXT_DURATION_S, delay: DESCRIPTION_DELAY_S, ease: 'easeOut' as const },
  },
  exiting: {
    y: 0,
    opacity: 0,
    transition: { duration: TEXT_EXIT_DURATION_S, ease: 'easeOut' as const },
  },
} as const;

// Reveal de los CTAs del slide final: entran DESPUÉS de la descripción
// (que termina en DESCRIPTION_DELAY_S + TEXT_DURATION_S) y en secuencia
// (primario → "omitir") con un pequeño stagger. `custom` = índice del botón.
const CTA_BUTTON_BASE_DELAY_S = DESCRIPTION_DELAY_S + TEXT_DURATION_S;
const CTA_BUTTON_STAGGER_S = 0.15;

const CTA_BUTTON_VARIANTS = {
  hidden: { y: 16, opacity: 0 },
  visible: (i: number) => ({
    y: 0,
    opacity: 1,
    transition: {
      duration: TEXT_DURATION_S,
      delay: CTA_BUTTON_BASE_DELAY_S + i * CTA_BUTTON_STAGGER_S,
      ease: 'easeOut' as const,
    },
  }),
} as const;

/**
 * Tiempo aproximado que tarda el spring del track (stiffness 320,
 * damping 36, mass 0.9) en asentarse. Lo usamos como delay para
 * reset/pause de los videos: así el video solo arranca cuando el
 * slide está ya centrado, y deja de verse solo cuando ya está fuera
 * del viewport.
 */
const SLIDE_SETTLE_MS = 500;

/**
 * Duración del slide-up del DialogContent en variante fullscreen (ver
 * `&__content--fullscreen` en Dialog.module.scss). Lo usamos como retardo
 * antes de activar el primer slide, para que las animaciones internas
 * (video, título, descripción) no empiecen a la vez que la modal sube
 * desde abajo: arrancan cuando la modal ya está arriba del todo.
 */
const MODAL_ENTRANCE_MS = 320;

interface WelcomeSlideProps {
  step: import('./WelcomeOnboardingDialog.types').WelcomeOnboardingStep;
  isActive: boolean;
  /** Ancho del stage en px; el slide ocupa exactamente este valor. */
  width: number;
  reduceMotion: boolean;
}

/**
 * Render de un slide individual. Vive aparte del componente principal
 * para poder mantener un ref propio al `<video>` y reiniciarlo cuando
 * el slide vuelve a estado activo (p. ej. al hacer swipe atrás). El
 * atributo `autoPlay` solo arranca el video al montar; sin el reset
 * el video se quedaría congelado en el último frame en re-entradas.
 */
function WelcomeSlide({ step, isActive, width, reduceMotion }: WelcomeSlideProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasVideo = Boolean(step.video);
  // El video arranca visible solo si el slide nace activo (slide 1 al
  // abrir). El resto inician invisibles (opacity 0) y suben a 1 cuando
  // el slide se asienta tras un swipe/click.
  const [videoOpacity, setVideoOpacity] = useState(isActive && hasVideo ? 1 : 0);
  const isVideoInitialMount = useRef(true);
  // El contenedor del video solo se hace visible cuando el video YA está
  // reproduciéndose (evento `playing`). Sin esto, en una instalación limpia
  // (sin caché) el WebView de Android muestra su "play" nativo enorme mientras
  // el video aún descarga, hasta que arranca. Con el gate, el hueco queda vacío
  // (fondo del slide) hasta que hay imagen en movimiento. Una vez listo, se
  // queda listo (las re-entradas ya no dependen de la descarga).
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    if (!hasVideo) return;
    const v = videoRef.current;

    if (isVideoInitialMount.current) {
      isVideoInitialMount.current = false;
      // Si el video ya está cacheado, `canplay` puede haberse disparado antes
      // de montar el listener: comprobamos el readyState para no dejar el
      // contenedor oculto en ese caso. (readyState >= 3 = HAVE_FUTURE_DATA.)
      if (v && v.readyState >= 3) setVideoReady(true);
      // autoPlay del <video> ya dispara el play en el mount inicial. Si
      // el slide con video no es el primero, pausamos para no quemar
      // CPU reproduciéndolo invisible hasta que el usuario navegue.
      if (!isActive && v) v.pause();
      return;
    }

    if (!v) return;

    if (isActive) {
      // Re-entrada: el rebobinado al frame 0 lo hacemos AHORA (mientras
      // el contenedor sigue opacity:0) para darle al navegador todo el
      // delay del settle para decodificar ese frame. Si lo hiciéramos
      // a la vez que el fade-in, el usuario vería un frame intermedio
      // entre "lo último que reprodujo" y "frame 0" durante el fade.
      setVideoOpacity(0);
      v.currentTime = 0;
      const t = setTimeout(() => {
        setVideoOpacity(1);
        // play() puede rechazar si el navegador bloquea autoplay; tragamos
        // el error porque el video es decorativo (muted+playsInline).
        void v.play().catch(() => {});
      }, SLIDE_SETTLE_MS);
      return () => clearTimeout(t);
    }

    // Salida: el video se queda visible mientras el slide sale del
    // viewport. Una vez fuera, pausamos, bajamos opacidad y rebobinamos
    // — todo invisible — para que la próxima entrada parta de un estado
    // limpio aunque el usuario haga navegación rápida.
    const t = setTimeout(() => {
      setVideoOpacity(0);
      v.pause();
      v.currentTime = 0;
    }, SLIDE_SETTLE_MS);
    return () => clearTimeout(t);
  }, [isActive, hasVideo]);

  // Estado de animación del texto. Título y descripción comparten estado
  // (entran y salen juntos); las variantes propias les ponen distinta
  // delay para que la descripción arranque cuando el título está
  // terminando.
  const [textState, setTextState] = useState<TextAnimState>(isActive ? 'visible' : 'hidden');
  const isTextInitialMount = useRef(true);

  useEffect(() => {
    if (isTextInitialMount.current) {
      isTextInitialMount.current = false;
      // En el primer render el textState ya quedó correcto por useState.
      // Saltamos la lógica para no disparar "exiting" en un slide que
      // simplemente está naciendo inactivo (caso típico: slide 1 mientras
      // la modal aún hace el slide-up con dialogReady=false).
      return;
    }

    if (reduceMotion) {
      setTextState(isActive ? 'visible' : 'hidden');
      return;
    }

    if (isActive) {
      setTextState('visible');
      return;
    }

    // Slide saliendo: fade-out sin movimiento. Tras el settle del track,
    // rearmamos `hidden` para que la próxima entrada vuelva a hacer el
    // slide-up de 20px en lugar de quedarse en su sitio.
    setTextState('exiting');
    const t = setTimeout(() => setTextState('hidden'), SLIDE_SETTLE_MS);
    return () => clearTimeout(t);
  }, [isActive, reduceMotion]);

  // ============================================================
  // Layout "acción final": mismo video enmarcado que el layout estándar
  // (contenedor `welcome__video`, aspect 4/3, mismo reset+play). No lleva
  // textos; los dos CTAs (primary/ghost) los pinta el dialog en su footer
  // (ver `welcome__ctaFooter`), anclados abajo en vez de la flecha.
  // ============================================================
  if (step.cta) {
    return (
      <div
        className={styles.welcome__slide}
        aria-hidden={!isActive}
        style={{ width }}
      >
        <div className={styles.welcome__slideInner}>
          {step.video && (
            <div
              className={`${styles.welcome__video} ${styles['welcome__video--cta']}`}
              style={{ opacity: videoReady ? videoOpacity : 0 }}
              aria-hidden="true"
            >
              {/* A diferencia del layout estándar, aquí el `scale` se aplica
                  al <video> INTERNO, no al contenedor: el contenedor tiene
                  `overflow: hidden`, así el zoom se recorta a la caja en
                  lugar de desbordar hacia abajo y tapar el texto. */}
              <video
                ref={videoRef}
                style={
                  step.video.scale && step.video.scale !== 1
                    ? { transform: `scale(${step.video.scale})` }
                    : undefined
                }
                autoPlay
                muted
                playsInline
                preload="auto"
                // Revelamos el contenedor solo cuando el video ya tiene datos
              // suficientes para reproducir (evita el "play" nativo de carga).
              // `onCanPlay` es fiable aunque el autoplay se retrase, así el
              // video nunca queda oculto para siempre.
              onCanPlay={() => setVideoReady(true)}
              >
                {step.video.sources.map((s) => (
                  <source key={s.src} src={s.src} type={s.type} />
                ))}
              </video>
            </div>
          )}
          {(step.title || step.description) && (
            <div className={styles.welcome__text}>
              {step.title && (
                <motion.h2
                  className={styles.welcome__title}
                  variants={TITLE_VARIANTS}
                  initial={reduceMotion ? textState : 'hidden'}
                  animate={textState}
                  transition={reduceMotion ? { duration: 0 } : undefined}
                >
                  {step.title}
                </motion.h2>
              )}
              {step.description && (
                <motion.p
                  className={styles.welcome__description}
                  variants={DESCRIPTION_VARIANTS}
                  initial={reduceMotion ? textState : 'hidden'}
                  animate={textState}
                  transition={reduceMotion ? { duration: 0 } : undefined}
                >
                  {step.description}
                </motion.p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============================================================
  // Layout custom: slide con `content` propio (p. ej. selector de
  // categorías). Mantiene título + descripción opcionales arriba y deja
  // que el padre componga el resto del contenido. El footer del dialog
  // (dots + arrow) sigue activo a través del wrapper del dialog.
  // ============================================================
  if (step.content) {
    return (
      <div
        className={`${styles.welcome__slide} ${styles['welcome__slide--content']}`}
        aria-hidden={!isActive}
        style={{ width }}
      >
        <div className={styles.welcome__slideInner}>
          {(step.title || step.description) && (
            <header className={styles.welcome__contentHeader}>
              {step.title && (
                <h2 className={styles.welcome__title}>{step.title}</h2>
              )}
              {step.description && (
                <p className={styles.welcome__description}>{step.description}</p>
              )}
            </header>
          )}
          <div className={styles.welcome__contentBody}>
            <SlideActiveContext.Provider value={isActive}>
              {step.content}
            </SlideActiveContext.Provider>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={styles.welcome__slide}
      // Solo el slide activo se expone a SR; los demás quedan ocultos
      // para evitar lectura de pasos adyacentes.
      aria-hidden={!isActive}
      style={{ width }}
    >
      <div className={styles.welcome__slideInner}>
        {step.video ? (
          <div
            className={styles.welcome__video}
            style={{
              opacity: videoReady ? videoOpacity : 0,
              // Escalado opcional por slide (`step.video.scale`). El
              // contenedor mantiene su box, pero su visual crece desde
              // el centro: útil si un asset concreto se ve pequeño sin
              // tener que retocar el resto.
              ...(step.video.scale && step.video.scale !== 1
                ? { transform: `scale(${step.video.scale})` }
                : {}),
            }}
            aria-hidden="true"
          >
            <video
              ref={videoRef}
              // muted + playsInline son obligatorios para que el autoplay
              // funcione en iOS Safari y Android Chrome sin requerir
              // interacción del usuario.
              autoPlay
              muted
              playsInline
              preload="auto"
              // Revelamos el contenedor solo cuando el video ya tiene datos
              // suficientes para reproducir (evita el "play" nativo de carga).
              // `onCanPlay` es fiable aunque el autoplay se retrase, así el
              // video nunca queda oculto para siempre.
              onCanPlay={() => setVideoReady(true)}
            >
              {step.video.sources.map((src) => (
                <source key={src.src} src={src.src} type={src.type} />
              ))}
            </video>
          </div>
        ) : step.visual ? (
          <div className={styles.welcome__visual} aria-hidden="true">
            {step.visual}
          </div>
        ) : null}
        {/* Bloque de texto con gap propio más pequeño que el del
            slideInner: el espacio título ↔ descripción es más estrecho
            que el de visual/video ↔ texto. */}
        <div className={styles.welcome__text}>
          <motion.h2
            className={styles.welcome__title}
            variants={TITLE_VARIANTS}
            // initial matches state cuando reduceMotion para que no haya
            // ninguna animación de inicio; sin reduceMotion partimos
            // siempre desde `hidden` para hacer el slide-up de entrada.
            initial={reduceMotion ? textState : 'hidden'}
            animate={textState}
            // El override de transition solo se aplica con reduceMotion:
            // anula los delays/durations de los variants y todo es snap.
            transition={reduceMotion ? { duration: 0 } : undefined}
          >
            {step.title}
          </motion.h2>
          <motion.p
            className={styles.welcome__description}
            variants={DESCRIPTION_VARIANTS}
            initial={reduceMotion ? textState : 'hidden'}
            animate={textState}
            transition={reduceMotion ? { duration: 0 } : undefined}
          >
            {step.description}
          </motion.p>
        </div>
      </div>
    </div>
  );
}

export function WelcomeOnboardingDialog({
  open,
  steps,
  onComplete,
  onLater,
  dismissible = true,
  allowSwipe = true,
  isCompleting = false,
  nextLabel,
  finishLabel,
  paginationLabel,
  closeLabel,
}: WelcomeOnboardingDialogProps) {
  const isMobile = useIsMobile();
  const reduceMotion = useReducedMotion();
  const [currentStep, setCurrentStep] = useState(0);
  const stageRef = useRef<HTMLDivElement>(null);
  // Inicializamos con window.innerWidth para que en el primer render los
  // slides ya tengan un ancho razonable. Si no lo hiciéramos, `stageWidth=0`
  // colapsaría los 4 slides a width=0 y el contenido se vería todo apilado
  // al overflowear cada caja antes de que useLayoutEffect refine la medida.
  const [stageWidth, setStageWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 0
  );

  // Motion value imperativo: framer-motion lo "lee" para el transform, drag
  // lo modifica durante el gesto, y el useEffect de abajo lo anima cuando
  // cambia el paso. Usar la prop `animate={{x}}` con drag activo produce
  // conflictos (la motion value queda atada al gesto y la prop no dispara
  // siempre el tween).
  const x = useMotionValue(0);

  const total = steps.length;
  const safeIndex = Math.min(Math.max(currentStep, 0), Math.max(total - 1, 0));
  const step = steps[safeIndex];
  const isLast = safeIndex === total - 1;

  // Al abrir el modal volvemos siempre al primer paso: si "Ver más tarde"
  // cerró el modal a mitad, la próxima vez empezamos limpios desde 0.
  useEffect(() => {
    if (open) {
      setCurrentStep(0);
      x.set(0);
    }
  }, [open, x]);

  // Gate que se levanta cuando la modal termina su slide-up. Mientras
  // `dialogReady` sea false, todos los slides están "inactivos" para sus
  // animaciones internas — el slide-1 espera a este flag antes de
  // arrancar el video y el reveal de los textos, así no se solapan con
  // la entrada de la propia modal.
  const [dialogReady, setDialogReady] = useState(false);
  useEffect(() => {
    if (!open) {
      setDialogReady(false);
      return;
    }
    const t = setTimeout(() => setDialogReady(true), MODAL_ENTRANCE_MS);
    return () => clearTimeout(t);
  }, [open]);

  // Dos flujos separados que pueden correr juntos o por separado:
  //   - Welcome onboarding (stage 'welcome'): slides 1-6 del intro,
  //     incluido el slide final con vídeo a pantalla completa + CTA.
  //   - Personalización (stage 'personalization'): slides 7-10 con
  //     selectores de nivel, rutina, horario y categorías.
  // Los dots y la completitud del onboarding se calculan por stage.
  const currentStage = step?.stage ?? 'welcome';
  const stageFooterSlides = steps.filter(
    (s) => (s.stage ?? 'welcome') === currentStage && !s.cta && !s.hideFooter
  );
  const dotsTotal = stageFooterSlides.length;
  const dotsCurrent = step ? stageFooterSlides.findIndex((s) => s.id === step.id) : -1;

  // En cuanto el slide activo entra en el stage de personalización
  // marcamos el welcome onboarding como completado. El ref evita
  // múltiples PATCH en la misma sesión de dialog abierto y se resetea
  // al cerrar.
  const isPersonalizationStage = currentStage === 'personalization';
  const completedFiredRef = useRef(false);
  useEffect(() => {
    if (!open) {
      completedFiredRef.current = false;
    }
  }, [open]);
  useEffect(() => {
    if (!isPersonalizationStage || completedFiredRef.current) return;
    completedFiredRef.current = true;
    onComplete();
  }, [isPersonalizationStage, onComplete]);

  // Loading local mientras un step.onNext está pendiente. Cubre el
  // spinner del arrow para slides que persisten datos al avanzar (slide
  // 7 → guarda preferencias). Independiente de `isCompleting` (que es
  // el flag externo asociado a markCompleted del welcome).
  const [isAdvancing, setIsAdvancing] = useState(false);
  const showArrowSpinner = isAdvancing || (isLast && isCompleting);

  // Medimos el ancho del stage en cuanto el dialog abre y nos suscribimos a
  // resize (rotación / cambio de viewport en mobile). Ignoramos medidas de 0
  // para no colapsar los slides si Radix re-evalúa el portal durante una
  // animación de entrada.
  useLayoutEffect(() => {
    if (!open) return;
    const el = stageRef.current;
    if (!el) return;

    const update = () => {
      // Redondeamos a entero. Sin esto, el ancho puede ser subpíxel y se
      // propaga al `calc(100% + 16px)` del contenedor del video,
      // produciendo bordes con antialiasing visibles como hairlines
      // grises top/bottom (cross-browser).
      const w = Math.round(el.getBoundingClientRect().width);
      if (w > 0) setStageWidth(w);
    };
    update();
    // Doble medida: una en el próximo frame por si la primera ocurre
    // antes de que el portal de Radix haya asentado el layout final.
    const raf = requestAnimationFrame(update);

    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [open]);

  // Anima la motion value al paso activo cada vez que cambia safeIndex o el
  // ancho del stage. Esto cubre tanto los clicks en "Continuar" como el
  // snap post-drag.
  useEffect(() => {
    if (stageWidth === 0) return;
    const controls = fmAnimate(x, -safeIndex * stageWidth, SPRING);
    return () => controls.stop();
  }, [safeIndex, stageWidth, x]);

  const goNext = async () => {
    // Paso bloqueado (p. ej. pregunta obligatoria sin responder): no avanzar.
    // Cubre también el swipe, que llama a goNext sin pasar por el arrow.
    if (step?.nextDisabled === true) return;
    // Slides con onNext propio (p. ej. selector de nivel o de
    // preferencias) toman precedencia: el dialog espera al promise y
    // muestra el spinner del arrow mientras dura. Tras un onNext OK,
    // si no era el último slide avanzamos automáticamente al siguiente
    // — los handlers no tienen acceso a setCurrentStep, así que el
    // dialog lo hace por ellos. En el último slide no avanzamos: el
    // handler decide si cierra (típico) o si onComplete tomaría el
    // relevo (lo dispara el effect de personalization stage entry).
    if (step?.onNext) {
      setIsAdvancing(true);
      try {
        await step.onNext();
        if (!isLast) {
          setCurrentStep((s) => Math.min(total - 1, s + 1));
        }
      } catch (err) {
        console.error('Slide onNext failed:', err);
      } finally {
        setIsAdvancing(false);
      }
      return;
    }
    if (isLast) {
      onComplete();
      return;
    }
    setCurrentStep((s) => Math.min(total - 1, s + 1));
  };

  // Una vez el usuario avanza desde un slide tipo `cta` (acción final con
  // video a fondo) hacia el siguiente paso, ese cta queda bloqueado
  // hacia atrás: no es un paso del flujo, es un intro/decisión.
  const canGoPrev = safeIndex > 0 && !steps[safeIndex - 1]?.cta;

  const goPrev = () => {
    if (!canGoPrev) return;
    setCurrentStep((s) => Math.max(0, s - 1));
  };

  const handleDragEnd = (
    _e: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo
  ) => {
    const passedDistance = Math.abs(info.offset.x) > SWIPE_DISTANCE_PX;
    const passedVelocity = Math.abs(info.velocity.x) > SWIPE_VELOCITY;

    // Si no superamos el umbral, volvemos al paso actual (re-anima a -safeIndex*stageWidth).
    if (!passedDistance && !passedVelocity) {
      void fmAnimate(x, -safeIndex * stageWidth, SPRING);
      return;
    }

    if (info.offset.x < 0 && !isLast) {
      void goNext();
    } else if (info.offset.x > 0 && canGoPrev) {
      goPrev();
    } else {
      // Drag hacia el "borde" (primer paso, último paso o paso anterior
      // bloqueado por ser un cta). Snap suave de vuelta a la posición actual.
      void fmAnimate(x, -safeIndex * stageWidth, SPRING);
    }
  };

  if (total === 0 || !step) return null;

  return (
    <Dialog
      open={open}
      // Con `dismissible` los gestos de cierre equivalen a "ver más tarde".
      // Sin él (onboarding obligatorio) ignoramos el cierre: el modal solo
      // se baja cuando el padre pone `open=false` al completar el flujo.
      onOpenChange={(o) => {
        if (!o && dismissible) onLater();
      }}
    >
      <DialogContent
        variant="fullscreen"
        // Pintamos nuestra propia X (estilo header de StoryLayout) en
        // lugar de la transparente por defecto del Dialog molecule.
        hideDefaultClose
        // Por defecto Radix auto-enfoca el primer tabbable al abrir, que en
        // este modal es el botón redondo de avance. Ese foco programático
        // dispara :focus-visible y deja el anillo de a11y siempre dibujado
        // sobre el botón aunque el usuario no haya tocado el teclado.
        // Lo prevenimos para que el foco lo absorba la propia Content
        // (tabindex="-1"). Los usuarios de teclado podrán pasar al botón
        // con Tab, y entonces sí queremos el ring.
        onOpenAutoFocus={(e) => e.preventDefault()}
        // Radix interactions: con `dismissible`, cerrar con Esc / overlay / X =
        // "ver más tarde" (no persistimos nada; vuelve a aparecer al próximo
        // acceso). Sin `dismissible` bloqueamos todos los gestos de descarte
        // (preventDefault) para que la única salida sea completar el flujo.
        onEscapeKeyDown={(e) => {
          if (dismissible) onLater();
          else e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          e.preventDefault();
          if (dismissible) onLater();
        }}
        onInteractOutside={(e) => {
          if (!dismissible) e.preventDefault();
        }}
        aria-describedby={undefined}
      >
        {/* DialogTitle/Description son requeridos por Radix para a11y. Los
            mantenemos sr-only y reflejan el paso activo; cada slide pinta
            su propio h2/p visible. En slides tipo CTA (sin título), el
            aria-label del botón primario ya describe el paso, pero Radix
            necesita el DialogTitle presente — fallback a ese label. */}
        <DialogTitle className={styles['welcome__sr-only']}>
          {step.title ?? step.cta?.primaryLabel ?? ''}
        </DialogTitle>
        <DialogDescription className={styles['welcome__sr-only']}>
          {step.description ?? ''}
        </DialogDescription>

        <div className={styles.welcome}>
          {/* X de cerrar con el mismo estilo que el header de StoryLayout
              (cuadrado 40x40, bg-tertiary, radius-md, press-scale). Va
              absoluto en la esquina superior derecha; el dismiss lo gestiona
              Radix vía DialogClose → dispara onOpenChange(false) → onLater.
              En modo obligatorio (`dismissible=false`) no se pinta: no hay
              forma de descartar el modal, solo completarlo. */}
          {dismissible ? (
            <DialogClose asChild>
              <IconButton
                icon={X}
                variant="soft"
                className={styles.welcome__closeBtn}
                aria-label={closeLabel}
                onClick={(e) => {
                  if (e.detail > 0) e.currentTarget.blur();
                }}
              />
            </DialogClose>
          ) : null}

          <div ref={stageRef} className={styles.welcome__stage}>
            <motion.div
              className={styles.welcome__track}
              // width en px puros: stage * total. Evita depender de
              // porcentajes anidados (track 400% / slide 25%) que en algunos
              // navegadores y wrappers de Radix dan medidas raras.
              style={{ x, width: stageWidth * total }}
              // Drag deshabilitado cuando el slide activo es uno de acción
              // final (`cta`): no queremos que el alumno pueda volver
              // atrás con swipe desde slide_06 — el flujo termina ahí
              // pulsando uno de los dos CTAs.
              // Swipe disponible solo en el welcome onboarding (slides
              // 1-5). En personalization los pasos requieren acciones
              // explícitas (selecciones, guardado) y el avance debe
              // hacerse con el arrow del footer / botones del cta;
              // permitir swipe ahí lleva a cambios accidentales de paso.
              drag={
                allowSwipe &&
                isMobile &&
                stageWidth > 0 &&
                !step.cta &&
                step.stage !== 'personalization'
                  ? 'x'
                  : false
              }
              dragConstraints={{
                left: -(total - 1) * stageWidth,
                // Si el slide anterior es un cta (bloqueado hacia atrás),
                // limitamos el drag a la posición actual para que el
                // usuario no pueda ni asomar ese slide al hacer swipe.
                right: canGoPrev ? 0 : -safeIndex * stageWidth,
              }}
              // Elastic asimétrica: cuando el slide anterior es un cta
              // bloqueado, anulamos el elastic del lado derecho (hacia
              // atrás) para que no se asome ni un píxel del slide cta.
              // El lado izquierdo (forward) mantiene su rebote como en
              // el resto del flujo.
              dragElastic={
                canGoPrev
                  ? 0.18
                  : { left: 0.18, right: 0, top: 0, bottom: 0 }
              }
              dragMomentum={false}
              onDragEnd={handleDragEnd}
            >
              {steps.map((s, i) => (
                <WelcomeSlide
                  key={s.id}
                  step={s}
                  // Gate por `dialogReady`: ningún slide se considera
                  // activo hasta que la modal termina el slide-up.
                  isActive={dialogReady && i === safeIndex}
                  width={stageWidth}
                  reduceMotion={!!reduceMotion}
                />
              ))}
            </motion.div>
          </div>

          {/* Footer del dialog. Para slides tipo "acción final" (slide_06,
              con `cta`) el footer aloja los dos botones primary/ghost
              anclados abajo; para el resto, los dots + la flecha de avance. */}
          {step.hideFooter ? null : step.cta ? (
            <div className={styles.welcome__ctaFooter}>
              {/* Los CTAs entran en secuencia tras el reveal de la descripción.
                  Con reduceMotion parten ya 'visible' (sin animación). */}
              <motion.div
                custom={0}
                variants={CTA_BUTTON_VARIANTS}
                initial={reduceMotion ? 'visible' : 'hidden'}
                animate="visible"
              >
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  // Default: avanza al siguiente slide. El step puede pasar
                  // una acción custom (p. ej. navegar fuera del dialog).
                  onClick={step.cta.primaryAction ?? (() => void goNext())}
                >
                  {step.cta.primaryLabel}
                </Button>
              </motion.div>
              {step.cta.ghostLabel ? (
                <motion.div
                  custom={1}
                  variants={CTA_BUTTON_VARIANTS}
                  initial={reduceMotion ? 'visible' : 'hidden'}
                  animate="visible"
                >
                  <Button
                    variant="ghost"
                    size="lg"
                    fullWidth
                    // Default: equivalente a la X — cierra el dialog vía onLater
                    // (dismiss local sin persistir).
                    onClick={step.cta.ghostAction ?? onLater}
                  >
                    {step.cta.ghostLabel}
                  </Button>
                </motion.div>
              ) : null}
            </div>
          ) : (
            <div className={styles.welcome__footer}>
              {dotsTotal > 1 ? (
                <StepDots
                  total={dotsTotal}
                  current={dotsCurrent}
                  ariaLabel={paginationLabel}
                />
              ) : (
                // Placeholder vacío que mantiene `justify-content: space-between`
                // del footer empujando el arrow a la derecha cuando no hay dots
                // (p. ej. stage de personalización con 1 sólo slide).
                <span aria-hidden="true" />
              )}
              <motion.button
                type="button"
                className={styles.welcome__nextBtn}
                // Opt-out del :active global de los botones (ver _globals.scss).
                // El press-scale lo gestiona framer-motion para que en mobile
                // el feedback sea consistente con los banners del dashboard,
                // que también usan transform: scale($motion-press-scale) con
                // misma duración y easing.
                data-no-press-scale=""
                // Press-scale en todos los slides (incluidos los de
                // personalización): no lo gateamos por el estado de guardado,
                // así el feedback al pulsar es idéntico al de los slides de
                // bienvenida.
                whileTap={reduceMotion ? undefined : motionPressWhileTap}
                // Tras una activación con ratón/touch (detail > 0) quitamos el
                // foco para que :focus-visible no quede pegado al botón en
                // navegadores que persisten el foco post-click. Para activación
                // con teclado (Enter/Space → detail === 0) mantenemos el foco
                // para no perder el sitio entre pasos.
                onClick={(e) => {
                  if (e.detail > 0) e.currentTarget.blur();
                  void goNext();
                }}
                disabled={showArrowSpinner || step.nextDisabled === true}
                // Marca el estado de carga: deshabilitamos para evitar dobles
                // pulsaciones, pero NO atenuamos el botón (ver SCSS) para que no
                // "se ponga gris" mientras guarda/avanza.
                data-loading={showArrowSpinner ? '' : undefined}
                aria-label={isLast ? finishLabel : nextLabel}
              >
                {/* Siempre el icono de flecha (sin spinner): el guardado del
                    paso ocurre en segundo plano, igual que en los slides de
                    bienvenida, para que el botón no cambie al pulsarlo. */}
                <Icon icon={ArrowRight} size="md" />
              </motion.button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

WelcomeOnboardingDialog.displayName = 'WelcomeOnboardingDialog';
